import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

const GALLERY_IMAGES = [
  {
    src: "https://customer-assets.emergentagent.com/job_event-packages-club/artifacts/fag1ze6d_Screenshot%202026-03-27%20at%2023.07.45.png",
    alt: "Guests at the horsebox bar with scenic countryside view"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_event-packages-club/artifacts/885jwtup_IMG_2565%202.JPG",
    alt: "Party crowd at Charlesworth Club event"
  },
  {
    src: "https://customer-assets.emergentagent.com/job_event-packages-club/artifacts/wxx0tbzc_IMG_2713%202.jpg",
    alt: "Charlesworth Club event celebration"
  },
  {
    src: "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-5053-2_orig.jpg",
    alt: "Charlesworth Club horsebox bar at sunset"
  },
  {
    src: "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-6658-2_orig.jpg",
    alt: "Guests enjoying drinks at the bar"
  },
  {
    src: "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-5207-2_orig.jpg",
    alt: "Wedding reception setup"
  },
  {
    src: "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-6485_orig.jpg",
    alt: "Bar interior detail"
  },
  {
    src: "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-6480_orig.jpg",
    alt: "Evening event atmosphere"
  },
  {
    src: "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-7640_orig.jpg",
    alt: "Festival setup"
  }
];

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const openLightbox = (index) => {
    setSelectedImage(index);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };

  const navigateLightbox = (direction) => {
    if (direction === "next") {
      setSelectedImage((prev) => (prev + 1) % GALLERY_IMAGES.length);
    } else {
      setSelectedImage((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="gallery-page">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#050505]">
        <div className="container-custom text-center">
          <p className="label-text text-yellow-400 mb-4">Our Work</p>
          <h1 className="heading-xl text-white mb-6">Gallery</h1>
          <p className="text-body max-w-2xl mx-auto">
            Browse through our collection of events. From intimate parties to grand weddings, 
            see how we transform celebrations across Surrey and beyond.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-24 bg-[#050505]" data-testid="gallery-grid">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {GALLERY_IMAGES.map((image, index) => (
              <div
                key={index}
                onClick={() => openLightbox(index)}
                className="aspect-square img-zoom cursor-pointer group relative"
                data-testid={`gallery-image-${index}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-semibold uppercase tracking-wider text-sm">
                    View
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <h3 className="heading-md text-white mb-4">Want This for Your Event?</h3>
          <p className="text-body mb-8 max-w-xl mx-auto">
            Let us create an unforgettable experience for your celebration.
          </p>
          <Link to="/pricing" className="btn-primary" data-testid="gallery-book-now">
            Book Now
            <ChevronRight className="inline ml-2 w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={closeLightbox}
          data-testid="lightbox"
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-yellow-400 transition-colors"
            data-testid="close-lightbox"
          >
            <X size={32} />
          </button>

          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox("prev"); }}
            className="absolute left-6 text-white hover:text-yellow-400 transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={40} />
          </button>

          <img
            src={GALLERY_IMAGES[selectedImage].src}
            alt={GALLERY_IMAGES[selectedImage].alt}
            className="max-w-[90vw] max-h-[80vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox("next"); }}
            className="absolute right-6 text-white hover:text-yellow-400 transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight size={40} />
          </button>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-neutral-400 text-sm">
            {selectedImage + 1} / {GALLERY_IMAGES.length}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
